import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';

function Chat({ token, chatId }) {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const response = await fetch(`/api/chat/history?chatId=${chatId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const history = await response.json();
          // Format citations in history
          const formattedHistory = history.map(msg => ({
            ...msg,
            content: msg.role === 'assistant' ? formatCitations(msg.content) : msg.content
          }));
          setMessages(formattedHistory);
        }
      } catch (error) {
        console.error('Error fetching chat history:', error);
      }
    };

    fetchHistory();
  }, [chatId, token]);

  const formatCitations = (text) => {
    if (!text) return text;
    // Replace OpenAI citation format with [filename.pdf]
    let formatted = text.replace(/【[^†]*†([^】]+)】/g, '[$1]');
    // Remove [source] if it appears
    return formatted.replace(/\[source\]/g, '');
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    const aiMessage = { role: 'assistant', content: '' };
    setMessages(prev => [...prev, aiMessage]);

    try {
      const response = await fetch(`/api/chat/stream?message=${encodeURIComponent(input)}&chatId=${chatId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 429) {
          throw new Error('OpenAI API rate limit exceeded or insufficient quota. Please check your plan and billing details.');
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Chat request failed with status ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let done = false;
      let buffer = '';

      while (!done) {
        const { value, done: doneReading } = await reader.read();
        done = doneReading;
        buffer += decoder.decode(value, { stream: !done });
        
        let lines = buffer.split('\n');
        // Keep the last partial line in the buffer
        buffer = lines.pop();
        
        let extractedContent = '';
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          if (line.startsWith('data:')) {
            extractedContent += line.substring(5);
            // If the next line is also a data: line, it means this is a multi-line SSE event,
            // so we should add a newline back.
            if (i + 1 < lines.length && lines[i + 1].startsWith('data:')) {
              extractedContent += '\n';
            }
          }
        }
        
        if (extractedContent.length > 0) {
          setMessages(prev => {
            const lastMessage = prev[prev.length - 1];
            const updatedLastMessage = { ...lastMessage, content: formatCitations(lastMessage.content + extractedContent) };
            return [...prev.slice(0, -1), updatedLastMessage];
          });
        }
      }
    } catch (error) {
      console.error('Error in chat:', error);
      setMessages(prev => {
        // Remove the empty AI message we added before starting the fetch if it's still empty
        const newMessages = [...prev];
        if (newMessages.length > 0 && newMessages[newMessages.length - 1].role === 'assistant' && newMessages[newMessages.length - 1].content === '') {
          newMessages.pop();
        }
        return [...newMessages, { role: 'error', content: error.message || 'Failed to get response from AI.' }];
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="card shadow-sm mt-4">
      <div className="card-header bg-primary text-white">
        <h5 className="mb-0">ChatGPT Streaming Chat</h5>
      </div>
      <div className="card-body" style={{ height: '600px', overflowY: 'auto' }}>
        {messages.map((msg, index) => (
          <div key={index} className={`mb-2 d-flex ${msg.role === 'user' ? 'justify-content-end' : 'justify-content-start text-start'}`}>
            <div className={`p-3 rounded ${msg.role === 'user' ? 'bg-primary text-white' : (msg.role === 'error' ? 'bg-danger text-white' : 'bg-light text-dark')}`} style={{ maxWidth: '80%', whiteSpace: msg.role === 'user' ? 'pre-wrap' : 'normal', textAlign: 'left' }}>
              {msg.role === 'assistant' ? (
                <div className="markdown-container">
                  <ReactMarkdown
                    components={{
                      p: ({ node, ...props }) => <p className="mb-2" {...props} />,
                      ul: ({ node, ...props }) => <ul className="mb-2 ps-3" {...props} />,
                      ol: ({ node, ...props }) => <ol className="mb-2 ps-3" {...props} />,
                      li: ({ node, ...props }) => <li className="mb-0" {...props} />,
                      h1: ({ node, children, ...props }) => <h1 className="h6 mb-2 fw-bold" {...props}>{children}</h1>,
                      h2: ({ node, children, ...props }) => <h2 className="h6 mb-2 fw-bold" {...props}>{children}</h2>,
                      h3: ({ node, children, ...props }) => <h3 className="h6 mb-2 fw-bold" {...props}>{children}</h3>,
                      h4: ({ node, children, ...props }) => <h4 className="h6 mb-2 fw-bold" {...props}>{children}</h4>,
                      h5: ({ node, children, ...props }) => <h5 className="h6 mb-2 fw-bold" {...props}>{children}</h5>,
                      h6: ({ node, children, ...props }) => <h6 className="h6 mb-2 fw-bold" {...props}>{children}</h6>
                    }}
                  >
                    {msg.content}
                  </ReactMarkdown>
                </div>
              ) : (
                msg.content
              )}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
      <div className="card-footer">
        <form onSubmit={handleSubmit} className="d-flex">
          <input
            type="text"
            className="form-control me-2"
            placeholder="Type your message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={isLoading}
          />
          <button type="submit" className="btn btn-primary" disabled={isLoading}>
            {isLoading ? '...' : 'Send'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default Chat;
